import { FC, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import { getCanvasAnimationLayerSx } from "./canvasAnimationLayerSx";
import type { CanvasAnimationLayoutVariant, CanvasBackgroundTheme } from "./canvasBackgroundTheme";
import { resizeCanvasKeepingPositions } from "./resizeCanvasKeepingPositions";
import { resolveCanvasSurfaceSize } from "./resolveCanvasSurfaceSize";
import { subscribeVisibilityAwareAnimationLoop } from "./subscribeVisibilityAwareAnimationLoop";

export type MusicNotesAnimationProps = {
  hue: number;
  theme: CanvasBackgroundTheme;
  variant?: CanvasAnimationLayoutVariant;
  isMobile?: boolean;
  frameStride?: number;
};

const NOTE_CHARS = ["♩", "♪", "♫", "♬"] as const;

/** Speed multiplier for notes (lower = slower) */
const DESKTOP_SPEED_MULTIPLIER = 0.5;
const MOBILE_SPEED_MULTIPLIER = 0.5;

type NoteParticle = {
  x: number;
  y: number;
  size: number;
  speedY: number;
  wob: number;
  wobAmp: number;
  rot: number;
  rotSpeed: number;
  char: (typeof NOTE_CHARS)[number];
  life: number;
  maxLife: number;
};

type RippleParticle = {
  x: number;
  y: number;
  r: number;
  maxR: number;
  life: number;
  maxLife: number;
};

/** Music notes + ripple rings — Delightful-er Forms spec. */
export const MusicNotesAnimation: FC<MusicNotesAnimationProps> = ({
  hue,
  theme,
  variant = "viewportFixed",
  isMobile = false,
  frameStride = 1,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvasMaybe = canvasRef.current;
    if (!canvasMaybe) {
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const contextMaybe = canvasMaybe.getContext("2d");
    if (!contextMaybe) {
      return;
    }

    const canvasEl: HTMLCanvasElement = canvasMaybe;
    const context: CanvasRenderingContext2D = contextMaybe;

    function tc(alpha: number): string {
      switch (theme) {
        case "dark":
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
        case "light":
          return `hsla(${hue}, 58%, 42%, ${alpha * 0.62})`;
        case "darkdull":
          return `hsla(${hue}, 22%, 68%, ${alpha * 0.52})`;
        case "lightdull":
          return `hsla(${hue}, 20%, 48%, ${alpha * 0.48})`;
        default:
          return `hsla(${hue}, 68%, 78%, ${alpha * 0.72})`;
      }
    }

    function resizeCanvas(): void {
      const { width, height } = resolveCanvasSurfaceSize(canvasEl);
      canvasEl.width = width;
      canvasEl.height = height;
    }

    resizeCanvas();

    const speedFactor = isMobile ? MOBILE_SPEED_MULTIPLIER : DESKTOP_SPEED_MULTIPLIER;

    function mkNote(fullScreen: boolean): NoteParticle {
      return {
        x: 40 + Math.random() * (canvasEl.width - 80),
        y: fullScreen ? Math.random() * canvasEl.height : canvasEl.height * (0.5 + Math.random() * 0.7),
        size: Math.random() * 16 + 13,
        speedY: (Math.random() * 0.28 + 0.1) * speedFactor,
        wob: Math.random() * Math.PI * 2,
        wobAmp: Math.random() * 1.1 + 0.4,
        rot: (Math.random() - 0.5) * 0.32,
        rotSpeed: (Math.random() - 0.5) * 0.007,
        char: NOTE_CHARS[Math.floor(Math.random() * NOTE_CHARS.length)]!,
        life: 0,
        maxLife: 300 + Math.random() * 200,
      };
    }

    const notes: NoteParticle[] = Array.from({ length: 28 }, () => {
      const note = mkNote(true);
      note.life = Math.random() * note.maxLife;
      return note;
    });

    const ripples: RippleParticle[] = [];
    let nextRippleFrame = 50;

    function spawnRipple(): void {
      ripples.push({
        x: 100 + Math.random() * (canvasEl.width - 200),
        y: 100 + Math.random() * (canvasEl.height - 200),
        r: 0,
        maxR: 52 + Math.random() * 44,
        life: 0,
        maxLife: 88,
      });
    }

    function onResize(): void {
      resizeCanvasKeepingPositions(canvasEl, [...notes, ...ripples]);
    }

    window.addEventListener("resize", onResize);

    let frame = 0;

    const stopAnimationLoop = subscribeVisibilityAwareAnimationLoop({ frameStride }, (frameSteps) => {
      context.clearRect(0, 0, canvasEl.width, canvasEl.height);
      frame += frameSteps;

      if (frame >= nextRippleFrame) {
        spawnRipple();
        nextRippleFrame = frame + 68 + Math.floor(Math.random() * 95);
      }

      for (let rippleIndex = ripples.length - 1; rippleIndex >= 0; rippleIndex--) {
        const ripple = ripples[rippleIndex]!;
        const rippleProgress = ripple.life / ripple.maxLife;
        const ringAlpha = (1 - rippleProgress) * 0.26;

        for (let ring = 0; ring < 3; ring++) {
          const ringRadius = ripple.r * (1 - ring * 0.3);
          if (ringRadius > 1) {
            context.beginPath();
            context.arc(ripple.x, ripple.y, ringRadius, 0, Math.PI * 2);
            context.strokeStyle = tc(ringAlpha * (1 - ring * 0.28));
            context.lineWidth = 1;
            context.stroke();
          }
        }

        ripple.r += 0.55 * frameSteps;
        ripple.life += frameSteps;
        if (ripple.life >= ripple.maxLife) {
          ripples.splice(rippleIndex, 1);
        }
      }

      for (const note of notes) {
        const progress = note.life / note.maxLife;
        let alpha = progress < 0.12 ? progress / 0.12 : progress > 0.82 ? (1 - progress) / 0.18 : 1;
        alpha *= 0.62;

        context.save();
        context.translate(note.x, note.y);
        context.rotate(note.rot);
        context.font = `${note.size}px serif`;
        context.fillStyle = tc(alpha);
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(note.char, 0, 0);
        context.restore();

        note.life += frameSteps;
        note.y -= note.speedY * frameSteps;
        note.x += Math.sin(note.life * 0.03 + note.wob) * note.wobAmp * 0.28 * frameSteps;
        note.rot += note.rotSpeed * frameSteps;

        if (note.life >= note.maxLife || note.y < -40) {
          Object.assign(note, mkNote(true));
        }
      }
    });

    return () => {
      stopAnimationLoop();
      window.removeEventListener("resize", onResize);
    };
  }, [hue, theme, isMobile, frameStride]);

  return <Box component="canvas" ref={canvasRef} sx={getCanvasAnimationLayerSx(variant)} />;
};
